import {Asset, Filter} from "../../api";

interface IRegionsConfig {
  /**
   * all regions deployed
   */
  regions: Array<IRegionConfig>
  /**
   * Default/Main region key, this region is directly populated by the DAM
   */
  default: string
}

interface IRegionConfig {
  /**
   * Region key
   */
  key: string
  /**
   * Region name
   */
  name: string
  /**
   * Url prefix for building thumbnails
   */
  thumbnailUrl: string;
  /**
   * Url prefix for direct url link
   */
  downloadUrl: string;
  /**
   * Url prefix for api calls
   */
  apiUrl: string;
}

/**
 * Holder for region configs
 */
export class UrlHelper {
  private config: IRegionsConfig | undefined;
  private currentConfig: IRegionConfig | undefined;

  init(): Promise<any> {
    // region.json is deployed by terraform when storage account is created, for more information
    // see the readme.md
    return fetch("/region.json")
    .then(response => response.json())
    .then(data => {
      this.selectRegion(data);
    });
  }

  /**
   * Get all regions inside region.json
   */
  getRegions(): Array<IRegionConfig> | undefined {
    return this.config?.regions;
  }

  /**
   * Get portal API url for current region
   */
  getApiUrl(): string | undefined {
    return this.currentConfig?.apiUrl;
  }

  /**
   * Get media resolver download url for current region
   */
  getDownloadUrl(): string | undefined {
    return this.currentConfig?.downloadUrl;
  }

  /**
   * Get media resolver thumbnail url for current region
   */
  getThumbnailUrl(): string | undefined {
    return this.currentConfig?.thumbnailUrl;
  }

    /**
   * Get all the renditions for current region
   */
  getRenditionsUrl(): string | undefined {
    return this.currentConfig?.apiUrl + "/d/information";
  }

  /**
   * Get the preferred default Download URL
   */
  getDefaultDownloadUrl(asset: Asset): string {
    if (asset.extension === 'jpg' || asset.extension === 'jpeg' || asset.extension === 'tif') {
        return `${this.getApiUrl()}/d/f/${asset.id}/2000x2000`
    } else {
        return `${this.getDownloadUrl()}/${asset.id}/${asset.name}`
    }
}

  /**
   * Select the region based on the region parameter
   */
  private selectRegion(currentConfig: IRegionsConfig): void {
    this.config = currentConfig;

    let regionKey = this.getRegionKey(currentConfig);

    let regionSelected = currentConfig.regions.filter(region => region.key === regionKey);

    // Fallback to default if region selected by user is unknown
    if (regionKey !== currentConfig.default && (!regionSelected || regionSelected.length === 0)) {
      regionSelected = currentConfig.regions.filter(region => region.key === currentConfig.default);
    }

    if (regionSelected.length > 1) {
      console.warn("Error selecting Region, it looks like there are more than one region with key: " + regionKey);
    }

    this.currentConfig = regionSelected.length > 0 ? regionSelected[0] : undefined;
  }

  /**
   *  Get Region key set by region selector, if not selected, set it to the default.
   * @param currentConfig current region config
   * @private
   */
  private getRegionKey(currentConfig: IRegionsConfig): string {
    let urlParams: URLSearchParams = new URLSearchParams(window.location.search);

    let regionKey = urlParams.get("region");

    if (!regionKey) {
      regionKey = currentConfig.default;
    }

    return regionKey;
  }

  /**
   *  Get Media Center id from url
   * @private
   */
  public getMediaCenterId(): string {
    let location = window.location.pathname;
    let segments = location.split("/");

    return segments[segments.length - 1];
  }

  /**
   * Parse filter URL query string into key value pairs
   */
  public parseFilters(filterString: string): Filter[] {
    const filters = filterString.split(";") || [];
    return filters
        .filter(filter => !filter.includes("undefined"))
        .map(filter => {
          const strings = filter.split(":");
          return {fieldId: strings[0], value: strings[1]};
        });
  }
}
